import React from "react";
import PageLayout from "@/components/layout/PageLayout";

const PrivacyPolicy: React.FC = () => {
  return (
    <PageLayout title="Privacy Policy">
      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-4xl mb-8">Privacy Policy</h1>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-muted-foreground mb-6"><strong>Effective Date:</strong> July 6, 2025</p>
          
          <h2 className="text-2xl font-semibold mb-4 text-primary">1. Introduction</h2>
          <p className="text-foreground mb-6">
            RemoteViz.ai ("we," "us," or "our") provides an online platform that enables users to generate, analyze, and share remote viewing targets and associated content, including images and text ("Services").
          </p>
          <p className="text-foreground mb-6">
            This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our Services.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-primary">2. Information We Collect</h2>
          <p className="text-foreground mb-4">We collect the following types of information:</p>
          
          <div className="mb-6">
            <h3 className="text-xl font-medium mb-2 text-[#5F436C]">Account Information</h3>
            <ul className="list-disc pl-6 text-foreground space-y-1">
              <li>Email address and password used to create your account.</li>
              <li>Unique user identifiers (e.g., Supabase UID).</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-medium mb-2 text-[#5F436C]">Usage Data</h3>
            <ul className="list-disc pl-6 text-foreground space-y-1">
              <li>Log data such as IP address, browser type, operating system, access times, and referring URLs.</li>
              <li>Activity logs related to target generation, messages sent, and image uploads.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-medium mb-2 text-[#5F436C]">Content</h3>
            <ul className="list-disc pl-6 text-foreground space-y-1">
              <li>Images and text you upload or generate.</li>
              <li>Metadata associated with your content (file size, format, timestamps).</li>
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-medium mb-2 text-[#5F436C]">Payment Information</h3>
            <p className="text-foreground">
              If you purchase paid features, our payment processor may collect billing details (we do not store payment card numbers directly).
            </p>
          </div>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">3. How We Use Your Information</h2>
          <p className="text-foreground mb-4">We use your information to:</p>
          <ul className="list-disc pl-6 text-foreground space-y-1 mb-6">
            <li>Provide and improve the Services.</li>
            <li>Authenticate your identity and manage your sessions.</li>
            <li>Store, display, and process your uploaded content.</li>
            <li>Communicate with you about your account or updates.</li>
            <li>Monitor usage to detect fraud, abuse, or violations of our Terms.</li>
            <li>Comply with legal obligations.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">4. Sharing of Information</h2>
          <p className="text-foreground mb-4">We do not sell your personal information. We may share your data:</p>
          <ul className="list-disc pl-6 text-foreground space-y-1 mb-6">
            <li>With service providers (e.g., Supabase for storage and authentication, OpenAI for content processing) strictly for delivering the Services.</li>
            <li>If required by law or to protect rights, property, or safety.</li>
            <li>In the event of a merger, acquisition, or sale of assets, with appropriate notice.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">5. Data Retention</h2>
          <p className="text-foreground mb-6">
            We retain your data as long as your account is active or as needed to provide Services, comply with our legal obligations, resolve disputes, or enforce agreements.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">6. Security</h2>
          <p className="text-foreground mb-6">
            We use reasonable measures to protect your data, including encryption of content and secure authentication. However, no system can be guaranteed 100% secure.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">7. Your Rights and Choices</h2>
          <p className="text-foreground mb-4">Depending on your jurisdiction, you may have rights to:</p>
          <ul className="list-disc pl-6 text-foreground space-y-1 mb-4">
            <li>Access, correct, or delete your personal data.</li>
            <li>Object to or restrict processing.</li>
            <li>Export your data in a machine-readable format.</li>
          </ul>
          <p className="text-foreground mb-6">
            You can exercise these rights by contacting us at support@remoteviz.ai.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">8. Children's Privacy</h2>
          <p className="text-foreground mb-6">
            Our Services are not intended for children under 13. We do not knowingly collect personal data from children.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">9. Changes to this Policy</h2>
          <p className="text-foreground mb-6">
            We may update this policy from time to time. Changes will be posted on this page with an updated effective date.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-[#5F436C]">10. Contact Us</h2>
          <p className="text-foreground">
            If you have questions, please email support@remoteviz.ai.
          </p>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy;